import {
  ApolloError,
  ApolloQueryResult,
  NetworkStatus,
  useQuery,
} from '@apollo/client';
import { omit, uniqBy } from 'lodash';
import { useUser } from 'hooks/use-user.hook';
import gql from 'graphql-tag';
import { useAccountContext } from 'contexts/account';
import { useSessionContext } from 'contexts/session';
import { useAccountSettings } from './use-account-settings.hook';
import { mockGetContractRequirements } from './tests/mocks';

export interface MappedContractSubscriberRequirement {
  contractVersionId: number;
  displayName: string;
  internalName: string;
  contractType: string;
  fileUrl: string;
  scrollRequired: boolean;
  signatureType: string;
  contractVersionLanguage?: {
    id: number;
    fileUrl: string;
  };
  stateAbbreviation?: string;
  status: string;
  utilityAccountId: number;
}

interface HookReturnType {
  data?: MappedContractSubscriberRequirement[];
  loading: boolean;
  error?: ApolloError;
  refetch: (
    variables?:
      | Partial<Exact<{ userId: number; languageShortCode: string }>>
      | Partial<
          Exact<{
            utilityAccountId: number;
            languageShortCode: string;
          }>
        >
  ) => Promise<
    ApolloQueryResult<
      GetContractRequirementsQuery | GetContractRequirementsForSmbQuery
    >
  >;
}

export const GET_CONTRACT_REQUIREMENTS_FOR_SMB = gql`
  query getContractRequirementsForSmb(
    $utilityAccountId: Int!
    $languageShortCode: String
  ) {
    utilityAccount(id: $utilityAccountId) {
      id
      subscriberContractRequirement {
        dependencies {
          __typename
          status
          ... on ContractSubscriberRequirement {
            contract {
              id
              displayName
              internalName
              contractType
              currentVersion {
                id
                fileUrl
                scrollRequired
                signatureType
                contractVersionLanguage(languageShortCode: $languageShortCode) {
                  id
                  fileUrl
                }
              }
              state {
                id
                abbreviation
              }
            }
          }
        }
      }
      plan {
        vnmProductSubscriptions {
          status
        }
      }
    }
  }
`;

export const useContractRequirements = (): HookReturnType => {
  // Currently we support only one utility account per account and
  // it didn't make sense to go through all the contract requirements for verification
  // as we don't have a way to display them all and let user sign them all. Hence we are
  // only displaying the first utility account's contract requirements which is most likely
  // the one that user signed while creating the account.
  const { data: user } = useUser();
  const { userId } = useSessionContext();
  const languageShortCode: string = user?.languageShortCode || 'en';
  const { selectedAccountId } = useAccountContext();
  const { data: accountData } = useAccountSettings(selectedAccountId);
  const { customerType } = accountData?.account || {};
  const utilityAccounts = accountData?.account?.utilityAccounts || [];
  const utilityAccountId = utilityAccounts[0]?.id;
  const isSMB = customerType === 'smb';
  const smbContractRequirementData = useFetchDataForSMB(
    utilityAccountId,
    languageShortCode,
    isSMB
  );
  const residentialContractRequirementData = useFetchDataForResidential(
    userId,
    languageShortCode,
    isSMB
  );

  const {
    allRequirements: smbRequirements,
    loading: smbLoading,
    error: smbError,
    refetch: smbRefetch,
  } = smbContractRequirementData;

  const {
    allRequirements: residentialRequirements,
    loading: residentialLoading,
    error: residentialError,
    refetch: residentialRefetch,
  } = residentialContractRequirementData;

  return {
    data: smbRequirements || residentialRequirements,
    loading: smbLoading || residentialLoading,
    error: smbError || residentialError,
    refetch: async variables => {
      if (isSMB) {
        return smbRefetch(variables);
      } else {
        return residentialRefetch(variables);
      }
    },
  };
};

const useFetchDataForSMB = (
  utilityAccountId: number,
  languageShortCode: string,
  isSMB: boolean
) => {
  const { data, loading, error, refetch } = useQuery<
    GetContractRequirementsForSmbQuery,
    GetContractRequirementsForSmbQueryVariables
  >(GET_CONTRACT_REQUIREMENTS_FOR_SMB, {
    variables: {
      utilityAccountId,
      languageShortCode,
    },
    skip: !utilityAccountId || !isSMB,
  });

  const { vnmProductSubscriptions } = data?.utilityAccount?.plan || {};
  let allRequirements;
  if (
    vnmProductSubscriptions &&
    vnmProductSubscriptions[0]?.status === 'active'
  ) {
    allRequirements =
      data?.utilityAccount?.subscriberContractRequirement.dependencies.map(
        d => {
          if (d.__typename === 'ContractSubscriberRequirement') {
            return [
              {
                ...omit(d.contract, ['id', 'currentVersion', 'state']),
                ...omit(d.contract.currentVersion!, ['id']),
                contractVersionId: d.contract.currentVersion!.id,
                stateAbbreviation: d.contract.state?.abbreviation,
                status: d.status,
                utilityAccountId,
              },
            ];
          } else {
            return [];
          }
        }
      );

    if (allRequirements) {
      allRequirements = uniqBy(allRequirements, c => c.contractVersionId);
    }
  }

  return { allRequirements, loading, error, refetch };
};

const useFetchDataForResidential = (
  userId: number,
  languageShortCode: string,
  isSMB: boolean
) => {
  const { data, loading, error, refetch } = useQuery<
    GetContractRequirementsQuery,
    GetContractRequirementsQueryVariables
  >(GET_CONTRACT_REQUIREMENTS, {
    variables: {
      userId,
      languageShortCode,
    },
    skip: !userId || isSMB,
  });

  let allRequirements = data?.user?.accounts
    ?.filter(a => a.status === 'active')
    .map(a =>
      a.customPlans
        .filter(p => p.active)
        .filter(
          // Hide requirements for plans that are enrolled since that generally means
          // that contracts were signed offline.
          p => !p.vnmProductSubscriptions?.find(ps => ps?.status === 'active')
        )
        .map(p => {
          return p.utilityAccounts
            .map(ua => {
              return ua.subscriberContractRequirement.dependencies.flatMap(
                d => {
                  if (d.__typename === 'ContractSubscriberRequirement') {
                    return [
                      {
                        ...omit(d.contract, ['id', 'currentVersion', 'state']),
                        ...omit(d.contract.currentVersion!, ['id']),
                        contractVersionId: d.contract.currentVersion!.id,
                        stateAbbreviation: d.contract.state?.abbreviation,
                        status: d.status,
                        utilityAccountId: ua.id,
                      },
                    ];
                  } else {
                    return [];
                  }
                }
              );
            })
            .flat();
        })
        .flat()
    )
    ?.flat();

  if (allRequirements) {
    allRequirements = uniqBy(allRequirements, c => c.contractVersionId);
  }

  return { allRequirements, loading, error, refetch };
};

export const GET_CONTRACT_REQUIREMENTS = gql`
  query getContractRequirements($userId: Int!, $languageShortCode: String!) {
    user(id: $userId) {
      id
      accounts {
        id
        status
        customPlans {
          id
          active
          vnmProductSubscriptions {
            status
          }
          utilityAccounts {
            id
            subscriberContractRequirement {
              dependencies {
                __typename
                status
                ... on ContractSubscriberRequirement {
                  contract {
                    id
                    displayName
                    internalName
                    contractType
                    currentVersion {
                      id
                      fileUrl
                      scrollRequired
                      signatureType
                      contractVersionLanguage(
                        languageShortCode: $languageShortCode
                      ) {
                        id
                        fileUrl
                      }
                    }
                    state {
                      id
                      abbreviation
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const useContractRequirementsMock: HookReturnType = {
  data: [],
  loading: false,
  error: undefined,
  refetch: () =>
    Promise.resolve({
      data: mockGetContractRequirements,
      loading: false,
      error: undefined,
      networkStatus: NetworkStatus.ready,
    }),
};
